import React, {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Loader2, Plus, Save, Trash2} from 'lucide-react';
import clsx from 'clsx';
import AdminService from '@/modules/admin/services/adminService';
import {useAdminNotifications} from '@/modules/admin/contexts/AdminNotificationContext';
import {logError} from '@/shared/utils/logger';
import type {KitCostSettings as KitCostSettingsRow, KitPackageTier} from '@/shared/types/api';

/**
 * Super-admin "Kit Costs" page. Drives the math behind the patient
 * Custom Lab Package price. Two cards:
 *   - Singleton Settings (Quick Draw device cost, serum-per-device by
 *     gender, dead-space buffers, shipping + handling, spare default)
 *   - Per-device-count Package Tiers (1, 2, 3 devices = different
 *     packaging SKU costs)
 *
 * All values in CENTS or microliters. Inputs let admins enter dollars
 * for cents fields (we multiply by 100 at save time) so they aren't
 * fighting the cents math in their head.
 */

const centsToDollars = (cents: number): string => (cents / 100).toFixed(2);
const dollarsToCents = (dollars: string): number => Math.round(parseFloat(dollars) * 100);

const KitCostSettings: React.FC = () => {
    const qc = useQueryClient();
    const {showSuccess, showError} = useAdminNotifications();

    const {data, isLoading, isError} = useQuery({
        queryKey: ['admin', 'kit-cost-settings'],
        queryFn: () => AdminService.getKitCostSettings(),
    });

    // Local form state — initialized from query data, edited freely,
    // saved on submit. Avoid the "always dirty" trap of binding inputs
    // straight to the query cache.
    const [form, setForm] = useState<KitCostSettingsRow | null>(null);
    const [tiers, setTiers] = useState<KitPackageTier[]>([]);

    useEffect(() => {
        if (data?.settings) setForm(data.settings);
        if (data?.tiers) setTiers(data.tiers);
    }, [data]);

    const saveSettings = useMutation({
        mutationFn: (body: Partial<KitCostSettingsRow>) =>
            AdminService.updateKitCostSettings(body),
        onSuccess: () => {
            showSuccess('Kit cost settings saved');
            qc.invalidateQueries({queryKey: ['admin', 'kit-cost-settings']});
            qc.invalidateQueries({queryKey: ['biomatch', 'dashboard']});
        },
        onError: (err) => {
            logError('Failed to save kit cost settings', err);
            showError('Could not save kit cost settings');
        },
    });

    const saveTiers = useMutation({
        mutationFn: (rows: KitPackageTier[]) =>
            AdminService.updateKitPackageTiers(
                rows.map((r) => ({
                    device_count: r.device_count,
                    package_cost_cents: r.package_cost_cents,
                    label: r.label,
                }))
            ),
        onSuccess: () => {
            showSuccess('Package tiers saved');
            qc.invalidateQueries({queryKey: ['admin', 'kit-cost-settings']});
            qc.invalidateQueries({queryKey: ['biomatch', 'dashboard']});
        },
        onError: (err) => {
            logError('Failed to save package tiers', err);
            showError('Could not save package tiers');
        },
    });

    if (isLoading) {
        return (
            <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-mld-primary" />
            </div>
        );
    }
    if (isError || !form) {
        return <p className="text-sm text-mld-danger">Failed to load kit cost settings.</p>;
    }

    const update = <K extends keyof KitCostSettingsRow>(key: K, value: KitCostSettingsRow[K]) => {
        setForm((s) => (s ? {...s, [key]: value} : s));
    };

    const addTier = () => {
        const nextCount = (tiers.at(-1)?.device_count ?? 0) + 1;
        setTiers((rows) => [
            ...rows,
            {
                device_count: nextCount,
                package_cost_cents: 0,
                label: null,
                updated_at: new Date().toISOString(),
            },
        ]);
    };

    const removeTier = (deviceCount: number) => {
        setTiers((rows) => rows.filter((r) => r.device_count !== deviceCount));
    };

    const updateTier = <K extends keyof KitPackageTier>(
        deviceCount: number,
        key: K,
        value: KitPackageTier[K]
    ) => {
        setTiers((rows) =>
            rows.map((r) => (r.device_count === deviceCount ? {...r, [key]: value} : r))
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-mld-text-primary">
                    Custom Lab Kit Costs
                </h2>
                <p className="text-sm text-mld-text-secondary">
                    Drives the patient-facing price on the Custom Lab Package card. Update when
                    Quick Draw, shipping, or packaging costs change — patient pricing recomputes on
                    the next dashboard load.
                </p>
            </div>

            {/* ── Singleton settings ── */}
            <section className="rounded-2xl border border-mld-panel-border bg-white p-6">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-mld-text-secondary">
                    Per-kit costs
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    <CentsField
                        label="Quick Draw device unit cost"
                        cents={form.tasso_device_cost_cents}
                        onChange={(c) => update('tasso_device_cost_cents', c)}
                    />
                    <CentsField
                        label="Return shipping cost"
                        cents={form.return_shipping_cost_cents}
                        onChange={(c) => update('return_shipping_cost_cents', c)}
                    />
                    <CentsField
                        label="Handling overhead"
                        cents={form.handling_overhead_cents}
                        onChange={(c) => update('handling_overhead_cents', c)}
                    />
                </div>

                <h3 className="mb-4 mt-8 text-sm font-semibold uppercase tracking-wide text-mld-text-secondary">
                    Serum capacity (µL)
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    <NumberField
                        label="Serum per Quick Draw — male"
                        value={form.tasso_serum_per_device_male_ul}
                        onChange={(n) => update('tasso_serum_per_device_male_ul', n)}
                        suffix="µL"
                    />
                    <NumberField
                        label="Serum per Quick Draw — female"
                        value={form.tasso_serum_per_device_female_ul}
                        onChange={(n) => update('tasso_serum_per_device_female_ul', n)}
                        suffix="µL"
                    />
                    <NumberField
                        label="Dead space — IA analyzer (DXI)"
                        value={form.serum_dead_space_ia_ul}
                        onChange={(n) => update('serum_dead_space_ia_ul', n)}
                        suffix="µL"
                    />
                    <NumberField
                        label="Dead space — Chem analyzer (DXC)"
                        value={form.serum_dead_space_chem_ul}
                        onChange={(n) => update('serum_dead_space_chem_ul', n)}
                        suffix="µL"
                    />
                </div>

                <h3 className="mb-4 mt-8 text-sm font-semibold uppercase tracking-wide text-mld-text-secondary">
                    Spare device
                </h3>
                <label className="flex items-center gap-2 text-sm text-mld-text-primary">
                    <input
                        type="checkbox"
                        checked={form.spare_device_default_offered}
                        onChange={(e) =>
                            update('spare_device_default_offered', e.target.checked)
                        }
                        className="h-4 w-4 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary"
                    />
                    Include a spare Quick Draw by default — patient is charged for it; refunded if
                    returned in original packaging.
                </label>

                <div className="mt-6 flex justify-end">
                    <button
                        type="button"
                        onClick={() => saveSettings.mutate(form)}
                        disabled={saveSettings.isPending}
                        className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-semibold text-white hover:bg-mld-primary/90 disabled:opacity-50">
                        {saveSettings.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        Save settings
                    </button>
                </div>
            </section>

            {/* ── Package tiers ── */}
            <section className="rounded-2xl border border-mld-panel-border bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-mld-text-secondary">
                            Packaging cost by device count
                        </h3>
                        <p className="text-xs text-mld-text-secondary">
                            One row per kit configuration we ship (1 device, 2 devices, 3
                            devices …). Cost is the box / pouch / foam total — devices and
                            shipping are added separately.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={addTier}
                        className="inline-flex items-center gap-1.5 rounded-md border border-mld-panel-border px-3 py-1.5 text-xs font-medium text-mld-text-primary hover:bg-mld-panel-bg">
                        <Plus className="h-3 w-3" /> Add tier
                    </button>
                </div>

                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-mld-panel-border text-left text-xs uppercase tracking-wide text-mld-text-secondary">
                            <th className="py-2 font-medium">Device count</th>
                            <th className="py-2 font-medium">Package cost</th>
                            <th className="py-2 font-medium">Label</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {tiers.length === 0 && (
                            <tr>
                                <td colSpan={4} className="py-3 text-xs italic text-mld-text-secondary">
                                    No tiers configured — add at least one for the kit calculator.
                                </td>
                            </tr>
                        )}
                        {tiers.map((t) => (
                            <tr key={t.device_count} className="border-b border-mld-panel-border/60">
                                <td className="py-2">
                                    <input
                                        type="number"
                                        min={1}
                                        value={t.device_count}
                                        onChange={(e) =>
                                            updateTier(
                                                t.device_count,
                                                'device_count',
                                                Math.max(1, Number(e.target.value) || 1)
                                            )
                                        }
                                        className="w-20 rounded-md border border-mld-panel-border px-2 py-1 text-sm focus:border-mld-primary focus:outline-none"
                                    />
                                </td>
                                <td className="py-2">
                                    <div className="inline-flex items-center gap-1">
                                        <span className="text-mld-text-secondary">$</span>
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            value={centsToDollars(t.package_cost_cents)}
                                            onChange={(e) =>
                                                updateTier(
                                                    t.device_count,
                                                    'package_cost_cents',
                                                    Math.max(0, dollarsToCents(e.target.value || '0'))
                                                )
                                            }
                                            className="w-28 rounded-md border border-mld-panel-border px-2 py-1 text-sm focus:border-mld-primary focus:outline-none"
                                        />
                                    </div>
                                </td>
                                <td className="py-2">
                                    <input
                                        type="text"
                                        maxLength={60}
                                        placeholder="e.g., Two-device box"
                                        value={t.label ?? ''}
                                        onChange={(e) =>
                                            updateTier(
                                                t.device_count,
                                                'label',
                                                e.target.value || null
                                            )
                                        }
                                        className="w-full rounded-md border border-mld-panel-border px-2 py-1 text-sm focus:border-mld-primary focus:outline-none"
                                    />
                                </td>
                                <td className="py-2 text-right">
                                    <button
                                        type="button"
                                        onClick={() => removeTier(t.device_count)}
                                        className="rounded-md p-1.5 text-mld-danger hover:bg-mld-danger/5">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={() => saveTiers.mutate(tiers)}
                        disabled={saveTiers.isPending}
                        className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-semibold text-white hover:bg-mld-primary/90 disabled:opacity-50">
                        {saveTiers.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        Save tiers
                    </button>
                </div>
            </section>
        </div>
    );
};

interface CentsFieldProps {
    label: string;
    cents: number;
    onChange: (cents: number) => void;
}

/**
 * Cents input — matches the package-tier table pattern: `$` prefix
 * sits outside the input as plain text, the input has its own border.
 */
const CentsField: React.FC<CentsFieldProps> = ({label, cents, onChange}) => (
    <div>
        <label className="mb-1 block text-xs font-medium text-mld-text-primary">{label}</label>
        <div className="flex items-center gap-1.5">
            <span className="text-mld-text-secondary">$</span>
            <input
                type="number"
                min={0}
                step={0.01}
                value={centsToDollars(cents)}
                onChange={(e) => onChange(Math.max(0, dollarsToCents(e.target.value || '0')))}
                className="w-full rounded-md border border-mld-panel-border px-2 py-1 text-sm focus:border-mld-primary focus:outline-none"
            />
        </div>
    </div>
);

interface NumberFieldProps {
    label: string;
    value: number;
    onChange: (n: number) => void;
    suffix?: string;
}

/**
 * Number input with optional unit suffix. Suffix is positioned inside
 * the input's right padding so the input owns the visible border (no
 * wrapping border) — matches the package-tier rows below.
 */
const NumberField: React.FC<NumberFieldProps> = ({label, value, onChange, suffix}) => (
    <div>
        <label className="mb-1 block text-xs font-medium text-mld-text-primary">{label}</label>
        <div className="relative">
            <input
                type="number"
                min={0}
                value={value}
                onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
                className={clsx(
                    'w-full rounded-md border border-mld-panel-border px-2 py-1 text-sm focus:border-mld-primary focus:outline-none',
                    suffix && 'pr-10'
                )}
            />
            {suffix && (
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-mld-text-secondary">
                    {suffix}
                </span>
            )}
        </div>
    </div>
);

export default KitCostSettings;
